/**
 * The waivers.
 *
 * ---------------------------------------------------------------------
 * WHAT THIS REPLACES
 *
 * Nothing, and that is the point. Before 0221 a club had two waiver
 * screens — one inside Aquatics and one inside Shooting — because two
 * modules had each built their own. A club that wanted a cart waiver, a
 * fitness room waiver or a junior camp waiver had nowhere to put it.
 *
 * ---------------------------------------------------------------------
 * THE UNPAPERED LIST IS THE FIRST THING, NOT THE DOCUMENTS
 *
 * The documents are the thing a club sets up once a year. The unpapered
 * list is the thing somebody opens in February, and again in April, and
 * again on the Friday before the season — and IDEA-008 is explicit that
 * having it only AFTER the season starts is the failure:
 *
 *     a member standing at the pool on a Saturday being told to go home
 *     and check their email
 *
 * So the page opens on who is not covered, grouped by WHY. Four reasons
 * that need four different things done about them: never signed is an
 * envelope, ran out is an envelope, signed an old version is an envelope
 * with different wording, and a parent signed and the child has grown up
 * needs the child themselves. A single "not covered: 412" tells a
 * coordinator nothing they can act on.
 *
 * ---------------------------------------------------------------------
 * AS OF A DATE, BECAUSE FEBRUARY IS THE POINT
 *
 * "Who will be unpapered when the pool opens on the 24th of May" is the
 * question. Asking it today and reading the answer as though it were
 * about May is how a club discovers in May that two hundred waivers
 * expired in April.
 */

import { useMemo, useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { date as fmtDate } from '../lib/format.ts';
import { Card, Empty, ErrorBanner, Loading, Status } from '../components/ui.tsx';

interface Doc {
  id: string;
  activity_code: string;
  version: number;
  title: string;
  has_text: boolean;
  body_uri: string | null;
  effective_from: string;
  effective_to: string | null;
  renew_months: number | null;
  guardian_required_under_age: number | null;
  sport_name: string | null;
  in_force: boolean;
  signatures: string;
}

interface Person {
  person_id: string;
  name: string;
  membership_number: string | null;
  account_number: string | null;
  standing: string;
}

interface Unpapered {
  activities: string[];
  activity: string | null;
  as_of: string | null;
  people: Person[];
  summary: Array<{ standing: string; count: number }>;
}

/**
 * The four reasons, in the words a coordinator would use.
 *
 * `guardian_lapsed` is the one worth spelling out. Nobody reading a list
 * guesses what it means, and the action it implies is different from
 * every other row on the page: this person cannot be sent an envelope
 * addressed to their parent.
 */
const STANDING_SAYS: Record<string, { label: string; what: string; tone: 'warning' | 'serious' }> = {
  unsigned: {
    label: 'Never signed',
    what: 'Has not signed this waiver at all.',
    tone: 'serious',
  },
  expired: {
    label: 'Ran out',
    what: 'Signed, and the renewal period has passed.',
    tone: 'warning',
  },
  superseded: {
    label: 'On an old version',
    what: 'Signed an earlier text. The signature stands as a record and no longer covers them.',
    tone: 'warning',
  },
  guardian_lapsed: {
    label: 'Grown up',
    what: 'A parent signed for them, and they have passed the age that needed one. They sign ' +
      'this one themselves.',
    tone: 'serious',
  },
};

/** `shoot_skeet` is not a thing to print at somebody. */
function activityLabel(code: string): string {
  return code
    .replace(/^shoot_/, 'Shooting — ')
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
}

export function WaiversPage() {
  const [activity, setActivity] = useState<string | null>(null);
  const [asOf, setAsOf] = useState('');
  const [publishing, setPublishing] = useState(false);

  const docs = useApi<{ documents: Doc[] }>('/v1/waivers');
  const query =
    `/v1/waivers/unpapered?${activity ? `activity=${activity}&` : ''}${asOf ? `as_of=${asOf}` : ''}`;
  const gaps = useApi<Unpapered>(query, [activity, asOf]);

  const documents = docs.data?.documents ?? [];
  const activities = gaps.data?.activities ?? [];
  const chosen = gaps.data?.activity ?? null;

  const byActivity = useMemo(() => {
    const out = new Map<string, Doc[]>();
    for (const d of documents) {
      if (!out.has(d.activity_code)) out.set(d.activity_code, []);
      out.get(d.activity_code)!.push(d);
    }
    return [...out];
  }, [documents]);

  if (docs.loading && !docs.data) return <Loading />;

  return (
    <>
      <h1>Waivers</h1>
      <ErrorBanner error={docs.error} />
      <ErrorBanner error={gaps.error} />

      {documents.length === 0 ? (
        <Card title="No Waivers Yet">
          <Empty>
            A waiver is a document, a version and an activity — carts, the pool, the range, the
            fitness room, junior camps. Publish the first one and everything that has to check
            whether somebody signed it will use the same answer.
          </Empty>
          <div className="actions">
            <button onClick={() => setPublishing(true)}>Publish a waiver</button>
          </div>
        </Card>
      ) : (
        <>
          {/* ======================================================== */}
          {/* The report somebody actually opens this page for. */}
          <Card title="Who Is Not Covered">
            <div className="row row--wrap" style={{ gap: 12, alignItems: 'flex-end' }}>
              <label>
                <span className="muted small">Activity</span>
                <select value={chosen ?? ''} onChange={(e) => setActivity(e.target.value)}>
                  {activities.map((a) => (
                    <option key={a} value={a}>{activityLabel(a)}</option>
                  ))}
                </select>
              </label>
              <label>
                <span className="muted small">As of</span>
                <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
              </label>
              {asOf && (
                <button className="secondary small" onClick={() => setAsOf('')}>
                  Back to today
                </button>
              )}
              <span className="spacer" />
              <button className="secondary" onClick={() => setPublishing(true)}>
                Publish a waiver
              </button>
            </div>

            <p className="muted small">
              Ask it about the day the season opens, not about today. A waiver that runs out in
              April is covered this morning and is not covered when it matters.
            </p>

            {gaps.loading && !gaps.data ? <Loading rows={2} /> : (
              (gaps.data?.people.length ?? 0) === 0 ? (
                <Empty>
                  Everybody on an active membership is covered for {activityLabel(chosen ?? '')}
                  {gaps.data?.as_of ? ` as of ${fmtDate(gaps.data.as_of)}` : ''}.
                </Empty>
              ) : (
                <>
                  {/* Grouped by reason, because four reasons need four
                      different things done about them. */}
                  <div className="tiles">
                    {(gaps.data?.summary ?? []).map((s) => (
                      <div key={s.standing} className="tile">
                        <div className="tile__label">
                          {STANDING_SAYS[s.standing]?.label ?? s.standing}
                        </div>
                        <div className="tile__value">{s.count}</div>
                        <div className="tile__note">
                          {STANDING_SAYS[s.standing]?.what ?? ''}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="table">
                    <table className="data data--cards">
                      <thead>
                        <tr>
                          <th>Who</th>
                          <th>Membership</th>
                          <th>Why</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(gaps.data?.people ?? []).map((p) => (
                          <tr key={p.person_id}>
                            <td data-label="Who" className="cell--title">{p.name}</td>
                            <td data-label="Membership" className="muted small">
                              {p.membership_number ?? '—'}
                            </td>
                            <td data-label="Why">
                              <Status tone={STANDING_SAYS[p.standing]?.tone ?? 'warning'}>
                                {STANDING_SAYS[p.standing]?.label ?? p.standing}
                              </Status>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )
            )}
          </Card>
        </>
      )}

      {publishing && (
        <PublishForm
          activities={activities}
          onDone={() => { setPublishing(false); docs.reload(); gaps.reload(); }}
          onCancel={() => setPublishing(false)}
        />
      )}

      {/* ========================================================== */}
      {documents.length > 0 && (
        <Card title="The Documents">
          <p className="muted small">
            A signature is against a version, never against "the waiver". Publishing a revision
            closes the one before it and everybody still on the old text becomes visible above —
            without their signature being deleted, because it is still true about the year they
            signed it.
          </p>
          <div className="table">
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Activity</th>
                  <th>Version</th>
                  <th>In force</th>
                  <th>Renews</th>
                  <th>Guardian under</th>
                  <th className="num">Signed</th>
                </tr>
              </thead>
              <tbody>
                {byActivity.map(([code, versions]) =>
                  versions.map((d, i) => (
                    <tr key={d.id}>
                      <td data-label="Activity" className="cell--title">
                        {i === 0 ? activityLabel(code) : ''}
                        {i === 0 && d.sport_name
                          ? <span className="muted small"> · {d.sport_name}</span>
                          : null}
                        <div className="muted small">{d.title}</div>
                      </td>
                      <td data-label="Version" className="muted small">
                        {d.version}
                        {!d.has_text && !d.body_uri
                          ? <span className="muted"> · no text</span>
                          : d.has_text ? '' : <span className="muted"> · linked</span>}
                      </td>
                      <td data-label="In force" className="muted small">
                        {d.in_force
                          ? <Status tone="good">now</Status>
                          : `${fmtDate(d.effective_from)}${d.effective_to ? ` – ${fmtDate(d.effective_to)}` : ''}`}
                      </td>
                      <td data-label="Renews" className="muted small">
                        {d.renew_months ? `every ${d.renew_months} months` : 'until replaced'}
                      </td>
                      <td data-label="Guardian under" className="muted small">
                        {d.guardian_required_under_age ?? '—'}
                      </td>
                      <td data-label="Signed" className="num">{Number(d.signatures)}</td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}

/**
 * Publishing a version.
 *
 * The version number is not asked for. A club publishing next year's
 * cart waiver knows it is next year's; it does not know whether that
 * makes it version 3 or version 4, and asking produces a collision the
 * registry then has to refuse. The server takes the next one.
 *
 * The text, or a link to it. WAIVER-1 refuses both empty, and it is
 * worth refusing here too so the form says it before the round trip.
 */
function PublishForm({
  activities, onDone, onCancel,
}: {
  activities: string[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const { club } = useSession();
  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [uri, setUri] = useState('');
  const [from, setFrom] = useState('');
  const [renew, setRenew] = useState('12');
  const [guardian, setGuardian] = useState('18');

  const save = useMutation(async () => {
    await api('/v1/waivers', {
      method: 'POST',
      body: {
        activity_code: code,
        title,
        body: text.trim() === '' ? null : text.trim(),
        body_uri: uri.trim() === '' ? null : uri.trim(),
        effective_from: from,
        renew_months: renew === '' ? null : Number(renew),
        guardian_required_under_age: guardian === '' ? null : Number(guardian),
      },
    });
    onDone();
  });

  const hasWords = text.trim() !== '' || uri.trim() !== '';
  const ready = code !== '' && title.trim() !== '' && from !== '' && hasWords;

  return (
    <Card title="Publish a Waiver">
      <ErrorBanner error={save.error} />

      <div className="row row--wrap" style={{ gap: 12, alignItems: 'flex-end' }}>
        <label>
          <span className="muted small">Activity</span>
          <input list="waiver-activities" value={code} className="mono"
                 onChange={(e) =>
                   setCode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))} />
          <datalist id="waiver-activities">
            {activities.map((a) => <option key={a} value={a} />)}
            <option value="cart" />
            <option value="gym" />
            <option value="junior_camp" />
            <option value="guest_play" />
          </datalist>
        </label>
        <label style={{ flex: 1, minWidth: 240 }}>
          <span className="muted small">Title</span>
          <input value={title} maxLength={200} style={{ width: '100%' }}
                 onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label>
          <span className="muted small">In force from</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          <span className="muted small">Renews every (months)</span>
          <input value={renew} inputMode="numeric" style={{ width: 90 }}
                 onChange={(e) => setRenew(e.target.value.replace(/[^0-9]/g, ''))} />
        </label>
        <label>
          <span className="muted small">A guardian signs under</span>
          <input value={guardian} inputMode="numeric" style={{ width: 90 }}
                 onChange={(e) => setGuardian(e.target.value.replace(/[^0-9]/g, ''))} />
        </label>
      </div>

      <p className="muted small">
        Leave the renewal blank for a waiver signed once and good until the text is replaced.
        Leave the age blank if nobody else ever signs for anybody.
      </p>

      <h3 style={{ marginTop: 18 }}>The Words</h3>
      <p className="muted small">
        Paste the text {club?.brand_name ? `${club.brand_name}'s` : 'the club’s'} counsel
        wrote. It is stored as it stands, so that on the day somebody asks what was signed in
        March the answer is the March text and not a dead link. A link is accepted instead if the
        club hosts the document itself.
      </p>
      <textarea rows={8} value={text} style={{ width: '100%' }}
                onChange={(e) => setText(e.target.value)} />

      <div className="row row--wrap" style={{ gap: 12, alignItems: 'flex-end', marginTop: 10 }}>
        <label style={{ flex: 1, minWidth: 240 }}>
          <span className="muted small">Or a link to it</span>
          <input value={uri} maxLength={500} style={{ width: '100%' }}
                 onChange={(e) => setUri(e.target.value)} />
        </label>
      </div>

      <div className="actions">
        <button disabled={save.busy || !ready} onClick={() => save.run()}>
          {save.busy ? 'Publishing…' : 'Publish it'}
        </button>
        <button className="secondary" onClick={onCancel}>Cancel</button>
        {!hasWords && (
          <span className="muted small">
            The text, or a link to it. A waiver that can produce neither is a checkbox.
          </span>
        )}
      </div>
    </Card>
  );
}
